import React from 'react';
import { DashboardKpi } from '../../types/dashboard';
import { Button } from '../ui/Button';

interface DashboardKpiRowProps {
  items: DashboardKpi[];
}

const CARD_RADIUS = 'rounded-[20px]';
const CARD_HEIGHT = 'min-h-[224px] sm:min-h-[256px] lg:min-h-[300px]';

const ArrowButton: React.FC<{ label: string }> = ({ label }) => (
  <Button tone="custom"
    type="button"
    aria-label={`${label} details`}
    className="absolute bottom-0 right-0 z-10 inline-flex h-[42px] w-[42px] items-center justify-center rounded-tl-[10px] bg-c-1f1f1f text-base text-white transition-transform group-hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/90 focus-visible:ring-offset-2 focus-visible:ring-offset-c-1f1f1f"
  >
    <span aria-hidden>↗</span>
  </Button>
);

/** Teal: bold title + status line top-left, oversized faint value top-right, description pinned bottom. */
const TealCard: React.FC<{ item: DashboardKpi }> = ({ item }) => (
  <article
    className={`group relative flex min-w-0 flex-col justify-between overflow-hidden border border-c-808080/30 p-5 text-white shadow-card ${CARD_RADIUS} ${CARD_HEIGHT} bg-[radial-gradient(circle_at_20%_20%,rgba(77,207,190,0.75),transparent_46%),linear-gradient(150deg,rgb(var(--c-0f7f9d,15_127_157)),rgb(var(--c-0e6787,14_103_135))_45%,rgb(var(--c-17408a,23_64_138)))]`}
  >
    <p
      aria-hidden
      className="pointer-events-none absolute right-5 top-2 text-[5.5rem] font-bold leading-none tracking-tight text-white/25 sm:text-[6.5rem]"
    >
      {item.value}
    </p>
    <div className="relative z-10 pr-24">
      <p className="text-[1.6rem] font-bold leading-tight">{item.title}</p>
      <p className="mt-1 text-sm text-white/85">{item.subtitle}</p>
      <span className="sr-only">{item.value}</span>
    </div>
    <p className="relative z-10 max-w-[85%] text-sm leading-5 text-white/90">{item.description}</p>
    {/* <ArrowButton label={item.title} /> */}
  </article>
);

/** Dark: value leads, title under it; subtitle pinned bottom. */
const DarkCard: React.FC<{ item: DashboardKpi }> = ({ item }) => (
  <article
    className={`group relative flex min-w-0 flex-col justify-between overflow-hidden border border-c-808080/30 bg-c-1f1f1f p-5 text-white shadow-card ${CARD_RADIUS} ${CARD_HEIGHT}`}
  >
    <div>
      <p className="text-[3.25rem] font-bold leading-none tracking-tight text-c-8b9bf6 sm:text-[4rem]">{item.value}</p>
      <p className="mt-1.5 text-base font-bold">{item.title}</p>
      <p className="mt-2 max-w-[90%] text-sm leading-5 text-white/80">{item.description}</p>
    </div>
    <p className="text-sm text-white/60">{item.subtitle}</p>
    {/* <ArrowButton label={item.title} /> */}
  </article>
);

/** Violet: value leads, title under it; delta + description pinned bottom. */
const VioletCard: React.FC<{ item: DashboardKpi }> = ({ item }) => (
  <article
    className={`group relative flex min-w-0 flex-col justify-between overflow-hidden border border-c-808080/30 p-5 text-white shadow-card ${CARD_RADIUS} ${CARD_HEIGHT} bg-[radial-gradient(circle_at_78%_64%,rgb(var(--c-5796ff,87_150_255)/0.4),transparent_40%),linear-gradient(135deg,rgb(var(--c-8f6dff,143_109_255)),rgb(var(--c-5f68d9,95_104_217))_38%,rgb(var(--c-4c79d7,76_121_215)))]`}
  >
    <div>
      <p className="text-[3.25rem] font-bold leading-none tracking-tight sm:text-[4rem]">{item.value}</p>
      <p className="mt-1.5 text-base font-bold">{item.title}</p>
    </div>
    <div className="max-w-[85%]">
      <p className="text-xl font-bold leading-tight">{item.subtitle}</p>
      <p className="mt-1 text-sm leading-5 text-white/90">{item.description}</p>
    </div>
    {/* <ArrowButton label={item.title} /> */}
  </article>
);

/** Sky: title top, value mid, description bottom, on a light gradient with dark text. */
const SkyCard: React.FC<{ item: DashboardKpi }> = ({ item }) => (
  <article
    className={`group relative flex min-w-0 flex-col justify-between overflow-hidden border border-c-808080/30 p-5 text-c-1f1f1f shadow-card ${CARD_RADIUS} ${CARD_HEIGHT} bg-[radial-gradient(circle_at_85%_15%,rgb(var(--c-e4ecff,228_236_255)/0.75),transparent_36%),linear-gradient(145deg,rgb(var(--c-d8e4ff,216_228_255)),rgb(var(--c-aac0ef,170_192_239))_62%,rgb(var(--c-84a2df,132_162_223)))]`}
  >
    <p className="max-w-[60%] text-base font-bold leading-tight">{item.title}</p>
    <div>
      <p className="text-[3rem] font-bold leading-none tracking-tight sm:text-[3.5rem]">{item.value}</p>
      <p className="mt-1 text-sm text-c-1f1f1f/80">{item.subtitle}</p>
    </div>
    <p className="max-w-[85%] text-sm leading-5">{item.description}</p>
    {/* <ArrowButton label={item.title} /> */}
  </article>
);

const cardByVariant: Record<DashboardKpi['variant'], React.FC<{ item: DashboardKpi }>> = {
  teal: TealCard,
  dark: DarkCard,
  violet: VioletCard,
  sky: SkyCard,
};

export const DashboardKpiRow: React.FC<DashboardKpiRowProps> = ({ items }) => {
  return (
    <section className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:mx-auto xl:max-w-[1466px] xl:grid-cols-4">
      {items.map((item) => {
        const Card = cardByVariant[item.variant];
        return <Card key={item.id} item={item} />;
      })}
    </section>
  );
};
